import { DOCUMENT } from '@angular/common';
import { Directive, EventEmitter, inject, Input, Output, SecurityContext } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { extractFileNameFromUrl, promiseWithCatch } from '../../app.helpers';

@Directive({
  selector: '[download]',
})
export class FileDownloadingDirective {
  @Input({ required: true })
  set url(value: string) {
    if (value.length) {
      this.handleUrl(value);
    }
  }

  @Output() public downloadFailed: EventEmitter<string> = new EventEmitter<string>();
  @Output() public downloadChanged: EventEmitter<boolean> = new EventEmitter<boolean>();

  private readonly PREVIEW_FILE_EXTENSIONS: string[] = [
    'pdf',
    'png',
    'jpg',
    'mp4',
    'jpeg',
    'gif',
    'webm',
    'vmw',
    'mpg',
    'mpeg',
    'mkv',
    'avi',
    'flv',
    'mov',
  ];

  private inProgress: Map<string, boolean> = new Map();
  private extractFileName: (url: string) => string = extractFileNameFromUrl;

  private document: Document = inject(DOCUMENT);
  private sanitizer: DomSanitizer = inject(DomSanitizer);

  private handleUrl(url: string): void {
    if (this.isFileAllowedPreview(url)) {
      this.openFile(url);
      return;
    }

    promiseWithCatch(this.downloadDirectly(url));
  }

  private openFile(url: string): void {
    window.open(this.buildSafeUrl(url), '_self');
  }

  private async downloadDirectly(url: string): Promise<void> {
    this.inProgress.set(url, true);
    this.downloadChanged.emit(true);

    const blobFile: Blob = await this.fetchFile(url);
    this.inProgress.delete(url);

    if (blobFile.size) {
      const blob: Blob = new Blob([blobFile], { type: 'application/octet-stream' });
      const anchor: HTMLAnchorElement = this.document.createElement('a');

      anchor.setAttribute('style', 'display: none');
      anchor.href = this.buildSafeUrl(window.URL.createObjectURL(blob));
      anchor.download = this.extractFileName(url);

      this.document.body.appendChild(anchor);
      anchor.click();

      setTimeout(() => {
        window.URL.revokeObjectURL(anchor.href);
        this.document.body.removeChild(anchor);
      }, 0);
    }

    if (this.inProgress.size === 0) {
      this.downloadChanged.emit(false);
    }
  }

  private async fetchFile(url: string): Promise<Blob> {
    return fetch(url, { method: 'GET' })
      .then((response: Response) => {
        if (response.status !== 200) {
          throw new Error(response.statusText);
        }

        return response.blob();
      })
      .catch((error: Error) => {
        this.downloadFailed.emit(error.message);
        return new Blob([]);
      });
  }

  private isFileAllowedPreview(url: string): boolean {
    return this.PREVIEW_FILE_EXTENSIONS.some((ext: string) => ext === this.extractFileName(url).split('.').pop());
  }

  private buildSafeUrl(url: string): string {
    return this.sanitizer.sanitize(SecurityContext.RESOURCE_URL, this.sanitizer.bypassSecurityTrustResourceUrl(url));
  }
}
